import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Header } from "common/Header";
import Switch from "@mui/material/Switch";
import CollapsablePanel from "common/layouts/CollapsablePanel";
import { isPolicyNameValid } from "../../Rules/utils";
import { safeStr, checkNothingPending } from "common/api";
import { responsePolicyMap, DEFAULT_POLICY } from "./utils";
import {
  getPolicies,
  BlockModal,
  parsePolicy,
  getPolicy,
  setBrateValue,
  setBdurValue,
  setBthrValue,
  setBthrwValue,
  SSHRATE_DFLT_VAL,
  SSHZERO_DFLT_VAL,
  SSHBTHRW_DFLT_VAL,
} from "../../PolicyEdit/utils";
import NumberInputWithSpinner from "common/NumberInputWithSpinnerState";
import PolicyName from "views/Config/PolicyEdit/EditName";

const Wrapper = styled.div`
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const ChangesWrapper = styled.div`
  padding: 0;
`;

const PendingChanges = styled.div`
  display: flex;
  margin-top: 7px;
`;

const Circle = styled.div`
  height: 16px;
  width: 16px;
  background: #f44336;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
`;

const getReturnUrlHeader = (returnView, pathName, queryParams) => {
  if (pathName === "/viewStatusSubscriberGroups") {
    queryParams.delete("view");
    return `/viewStatusSubscriberGroups?${queryParams.toString()}`;
  } else if (returnView) {
    return `${returnView}?${queryParams.toString()}`;
  } else {
    return undefined;
  }
};

const PolicyRateEdit = ({
  returnView: returnViewProps,
  tab,
  policy: policyProps,
  showReturn,
  create = undefined,
}) => {
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [policies, setPolicies] = useState({});
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());
  const policy = params.policy === undefined ? policyProps : params.policy;
  const defaultPolicy = { ...DEFAULT_POLICY, name: create || policy };
  const [initPolicy, setInitPolicy] = useState(defaultPolicy);
  const [editedPolicy, setEditedPolicy] = useState(defaultPolicy);
  let returnView =
    params.returnView === undefined ? returnViewProps : params.returnView;

  const isEdit = policy !== undefined && policy !== "";
  const location = useLocation();
  if (location.pathname === "/viewStatusSubscriberGroups") {
    returnView = "viewStatusSubscriberGroups";
  }

  useEffect(() => {
    getPolicies("rate")
      .then((response) => {
        setPolicies(response);
        if (policy) {
          const editPolicy = response[policy];
          if (!editPolicy) {
            showModalError("Error:", `Unknown policy ${policy}`);
          } else {
            return getPolicy(policy);
          }
        }
      })
      .then((response) => {
        if (response) {
          let parsedPolicy = parsePolicy(response, responsePolicyMap);
          if (parsedPolicy.shapdBrate) {
            parsedPolicy.shapdBrateUnit = parsedPolicy.shapdBrate.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shapdBrate = parseFloat(parsedPolicy.shapdBrate);
          }
          if (parsedPolicy.shapuBrate) {
            parsedPolicy.shapuBrateUnit = parsedPolicy.shapuBrate.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shapuBrate = parseFloat(parsedPolicy.shapuBrate);
          }
          if (parsedPolicy.shapdBthr) {
            parsedPolicy.shapdBthrUnit = parsedPolicy.shapdBthr.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shapdBthr = parseFloat(parsedPolicy.shapdBthr);
          }
          if (parsedPolicy.shapuBthr) {
            parsedPolicy.shapuBthrUnit = parsedPolicy.shapuBthr.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shapuBthr = parseFloat(parsedPolicy.shapuBthr);
          }
          if (parsedPolicy.shapdRateFlow !== undefined) {
            parsedPolicy.flowLimitEnabled = !isNaN(parsedPolicy.shapdRateFlow);
            parsedPolicy.shapdRateFlow =
              parsedPolicy.shapdRateFlow === "no"
                ? 100000
                : Number(parsedPolicy.shapdRateFlow);
          }
          const initPolicyNew = {
            ...DEFAULT_POLICY,
            ...parsedPolicy,
          };
          setInitPolicy(initPolicyNew);
          setEditedPolicy(initPolicyNew);
        }
      });
  }, []);

  const compareValues = (policyA, policyB) => {
    return Object.entries(policyA).find(([key, value]) => {
      if (
        key === "shapdBrate" ||
        key === "shapuBrate" ||
        key === "shapdBthr" ||
        key === "shapuBthr"
      ) {
        return Number(policyB[key]) != value;
      }
      return policyB[key] !== value;
    });
  };

  const changes =
    compareValues(editedPolicy, initPolicy) === undefined ? false : true;

  const handleBckEnabled = () => {
    if (!bckEnabled) {
      setShowBlockModal(true);
    } else {
      setEditedPolicy({ ...editedPolicy, ["bckEnabled"]: false });
    }
  };

  function checkRate(policy, unit, name, type) {
    if (isNaN(parseFloat(policy))) {
      showModalError("Warning:", `Invalid ${type} ${name.toLowerCase()}`);
      return false;
    } else if (unit !== "mbps") {
      if (Number(policy) < 0 || Number(policy) > 500) {
        showModalError(
          "Warning:",
          `${name} must be a percentage between 0% and 500%`,
        );
        return false;
      }
    } else {
      if (Number(policy) < 0.0 || Number(policy) > SSHRATE_DFLT_VAL) {
        showModalError(
          "Warning:",
          `${name} must be between 0 and ${SSHRATE_DFLT_VAL}`,
        );
        return false;
      }
    }
    return true;
  }

  const validatePolicy = (policyObj) => {
    const name = isEdit ? params.policy : policyObj.name;
    if (!isPolicyNameValid(name)) {
      showModalError("Warning:", "Invalid name");
      return false;
    }

    if (!policyObj.block) {
      if (policyObj.sshapd) {
        if (isNaN(policyObj.sshdrate)) {
          showModalError("Warning:", "Invalid download shaping rate");
          return false;
        } else if (Number(policyObj.sshdrate) < 0.001) {
          showModalError("Warning:", "Download shaping rate must be positive.");
          return false;
        } else if (Number(policyObj.sshdrate) > SSHRATE_DFLT_VAL) {
          showModalError(
            "Warning:",
            `Download shaping rate must below ${SSHRATE_DFLT_VAL} mbps.`,
          );
          return false;
        }
      }

      if (
        policyObj.flow &&
        (isNaN(policyObj.flowLimit) || Number(policyObj.flowLimit) < 1)
      ) {
        showModalError("Warning:", "Invalid flow limit");
        return false;
      }

      if (policyObj.sshapd) {
        const check = checkRate(
          policyObj.sshdbrate,
          policyObj.sshdbrateunit,
          "Burst Rate",
          "download",
        );
        if (!check) {
          return false;
        }
      }

      if (
        policyObj.sshapd &&
        (isNaN(policyObj.sshdbdur) ||
          Number(policyObj.sshdbdur) < 0 ||
          Number(policyObj.sshdbdur) > 86400)
      ) {
        showModalError("Warning:", "Invalid download burst duration");
        return false;
      }

      if (policyObj.sshapd) {
        const check = checkRate(
          policyObj.sshdbthr,
          policyObj.sshdbthrunit,
          "Burst Threshold",
          "download",
        );
        if (!check) {
          return false;
        }
      }

      if (
        policyObj.sshapd &&
        (isNaN(policyObj.sshdbthrw) || Number(policyObj.sshdbthrw) < 0)
      ) {
        showModalError("Warning:", "Invalid download burst threshold window");
        return false;
      }

      if (policyObj.sshapu) {
        if (isNaN(policyObj.sshurate)) {
          showModalError("Warning:", "Invalid download shaping rate");
          return false;
        } else if (Number(policyObj.sshurate) < 0.001) {
          showModalError("Warning:", "Download shaping rate must be positive.");
          return false;
        } else if (Number(policyObj.sshurate) > SSHRATE_DFLT_VAL) {
          showModalError(
            "Warning:",
            `Download shaping rate must below ${SSHRATE_DFLT_VAL} mbps.`,
          );
          return false;
        }
      }

      if (policyObj.sshapu) {
        const check = checkRate(
          policyObj.sshubrate,
          policyObj.sshubrateunit,
          "Burst Rate",
          "upload",
        );
        if (!check) {
          return false;
        }
      }

      if (
        policyObj.sshapu &&
        (isNaN(policyObj.sshubdur) ||
          Number(policyObj.sshubdur) < 0 ||
          Number(policyObj.sshubdur) > 86400)
      ) {
        showModalError("Warning:", "Invalid upload burst duration");
        return false;
      }

      if (policyObj.sshapu) {
        const check = checkRate(
          policyObj.sshubthr,
          policyObj.sshubthrunit,
          "Burst Threshold",
          "upload",
        );
        if (!check) {
          return false;
        }
      }

      if (
        policyObj.sshapu &&
        (isNaN(policyObj.sshubthrw) || Number(policyObj.sshubthrw) < 0)
      ) {
        showModalError("Warning:", "Invalid upload burst threshold window");
        return false;
      }
    }

    return true;
  };

  const handleApply = () => {
    const newPolicy = {
      name: isEdit ? params.policy : editedPolicy.name,
      block: bckEnabled,
      sshapd: rateSubsDownEnabled,
      sshdrate: shapdRate,
      flow: flowLimitEnabled,
      flowLimit: shapdRateFlow,
      sshdbrate: shapdBrate,
      sshdbrateunit: shapdBrateUnit,
      sshdbdur: shapdBdur,
      sshdbthr: shapdBthr,
      sshdbthrunit: shapdBthrUnit,
      sshdbthrw: shapdBthrw,
      sshapu: rateSubsUpEnabled,
      sshurate: shapuRate,
      sshubrate: shapuBrate,
      sshubrateunit: shapuBrateUnit,
      sshubdur: shapuBdur,
      sshubthr: shapuBthr,
      sshubthrunit: shapuBthrUnit,
      sshubthrw: shapuBthrw,
      sshdcong: acmEnabled,
    };

    if (!validatePolicy(newPolicy)) return;

    if (isEdit !== true) {
      const policiesSet = new Set(Object.keys(policies));
      if (policiesSet.has(newPolicy.name)) {
        showModalError("Error:", "Duplicated policy name");
        return;
      }
    }

    let cmd = `set policy rate ${safeStr(isEdit ? params.policy : newPolicy.name)}`;

    if (newPolicy.block) {
      cmd +=
        " on off default default default default default " +
        "off default default default default default";
    } else {
      cmd += " off";

      if (newPolicy.sshapd) {
        cmd += ` on ${Math.floor(
          Number(newPolicy.sshdrate) * 1000,
        ).toString()} ${
          newPolicy.sshdbrateunit === "mbps"
            ? setBrateValue(newPolicy.sshdbrate)
            : parseFloat(newPolicy.sshdbrate)
        }${newPolicy.sshdbrateunit === "mbps" ? "" : "%"} ${setBdurValue(
          newPolicy.sshdbdur,
        )} ${
          newPolicy.sshdbthrunit === "mbps"
            ? setBthrValue(newPolicy.sshdbthr)
            : parseFloat(newPolicy.sshdbthr)
        }${newPolicy.sshdbthrunit === "mbps" ? "" : "%"} ${setBthrwValue(
          newPolicy.sshdbthrw,
        )}`;
      } else {
        cmd += " off default default default default default";
      }

      if (newPolicy.sshapu) {
        //       SSHPU  SSHUR  SSHUBR SSHUBD SSHUBT
        cmd += ` on ${Math.floor(
          Number(newPolicy.sshurate) * 1000,
        ).toString()} ${
          newPolicy.sshubrateunit === "mbps"
            ? setBrateValue(newPolicy.sshubrate)
            : parseFloat(newPolicy.sshubrate)
        }${newPolicy.sshubrateunit === "mbps" ? "" : "%"} ${setBdurValue(
          newPolicy.sshubdur,
        )} ${
          newPolicy.sshubthrunit === "mbps"
            ? setBthrValue(newPolicy.sshubthr)
            : parseFloat(newPolicy.sshubthr)
        }${newPolicy.sshubthrunit === "mbps" ? "" : "%"} ${setBthrwValue(
          newPolicy.sshubthrw,
        )}`;
      } else {
        //       SSHPU SSHUR  SSHUBR SSHUBD SSHUBT
        cmd += " off default default default default default";
      }
    }

    // Disable dos function
    cmd += " off default default default";

    // Set auto-congestion
    cmd += newPolicy.sshdcong ? " on" : " off";

    cmd += " " + (newPolicy.flow === false ? "off" : newPolicy.flowLimit);

    checkNothingPending()
      .then(() => ifCl.run(cmd))
      .then((response) => {
        if (location.pathname === "/viewStatusSubscriberGroups") {
          const subGroupsParams = { ...queryParams };
          globalNavigate(
            "viewStatusSubscriberGroups",
            Object.keys(subGroupsParams).length > 0
              ? subGroupsParams
              : undefined,
          );
        } else if (returnView) {
          const { policy: _p, returnView: _rv, ...restParams } = params;
          globalNavigate(
            returnView,
            Object.keys(restParams).length > 0 ? restParams : undefined,
          );
        } else {
          globalNavigateBack();
        }
      })
      .catch((error) => {
        const updateNameError = error?.match(
          /%ERR-ENOENT: Cannot remove policy "(.*)" because it is part of a rule/,
        );
        if (updateNameError) {
          showModalError(
            "Error:",
            "Cannot update name policy because it is part of a rule.",
          );
        } else {
          showModalError("Error:", error);
        }
      });
  };

  const {
    bckEnabled,
    acmEnabled,
    rateSubsDownEnabled,
    rateSubsUpEnabled,
    flowLimitEnabled,
    shapdRate,
    shapdBrate,
    shapdBrateUnit,
    shapdBdur,
    shapdBthr,
    shapdBthrUnit,
    shapdBthrw,
    shapuRate,
    shapuBrate,
    shapuBrateUnit,
    shapuBdur,
    shapuBthr,
    shapuBthrUnit,
    shapuBthrw,
    shapdRateFlow,
  } = editedPolicy;

  const name = isEdit ? params.policy : editedPolicy.name;

  const queryParams = new URLSearchParams(searchParams);
  queryParams.delete("policy");
  queryParams.delete("returnView");

  const returnUrlHeader = getReturnUrlHeader(
    returnView,
    location.pathname,
    queryParams,
  );

  return (
    <Wrapper>
      <div className="row clearfix">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <div className="card container-lg">
            <Header
              title={`${
                policy && policy !== "" ? "Edit" : "New"
              } Subscriber Rate Policy`}
              subtitle={
                <>
                  Press <code>Apply </code> button at the end of the page to set
                  the new configuration values.
                </>
              }
              backButton={!!(returnView || showReturn)}
              returnUrl={returnUrlHeader}
            />
            <div className="body table-responsive padding-b-0">
              <div className="row margin-t-10">
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0">
                  {isEdit !== true ? (
                    <div className="input-group">
                      <span className="input-group-addon" title="Policy name">
                        Name
                      </span>
                      <input
                        id="poliRateName"
                        type="text"
                        size="55"
                        style={{ borderBottom: "1px solid #ddd" }}
                        className="align-left"
                        onChange={({ target }) =>
                          setEditedPolicy({
                            ...editedPolicy,
                            ["name"]: target.value,
                          })
                        }
                        value={name}
                      />
                    </div>
                  ) : (
                    <PolicyName
                      name={params.policy}
                      type="rate"
                      policies={policies}
                      returnView={returnView}
                      tab={params.tab}
                    />
                  )}
                </div>
                <div className="margin-0 padding-0">
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                    <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0 f-dpi-per-user">
                      <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                        <div
                          className="input-group"
                          style={{ paddingTop: "5px" }}
                        >
                          <span
                            className="input-group-addon align-left"
                            title="Block traffic"
                          >
                            Block*:{" "}
                            <span id="statusBlock" className="font-bold">
                              {bckEnabled ? "enabled" : "disabled"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                        <Switch
                          className="sw-input"
                          checked={bckEnabled}
                          onChange={handleBckEnabled}
                        />
                      </div>
                      <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                      <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                        <div
                          className="input-group"
                          style={{ paddingTop: "5px" }}
                        >
                          <span
                            className="input-group-addon align-left"
                            title="Block traffic"
                          >
                            Automatic Congestion Management*:{" "}
                            <span id="statusBlock" className="font-bold">
                              {acmEnabled ? "enabled" : "disabled"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                        <Switch
                          className="sw-input"
                          checked={acmEnabled}
                          onChange={() =>
                            setEditedPolicy({
                              ...editedPolicy,
                              ["acmEnabled"]: !acmEnabled,
                            })
                          }
                        />
                      </div>
                      <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                    </div>
                  </div>
                </div>
                {bckEnabled ? null : (
                  <Wrapper className="margin-0 padding-0">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Downlink shaping subscriber"
                              >
                                Maximum subscriber downlink speed:{" "}
                                <span className="font-bold">
                                  {rateSubsDownEnabled ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={rateSubsDownEnabled}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["rateSubsDownEnabled"]: target.checked,
                                })
                              }
                            />
                          </div>

                          {rateSubsDownEnabled ? (
                            <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shapdRate"
                                label="Rate"
                                min={0.0}
                                max={100000}
                                initialValue={shapdRate}
                                step={0.001}
                                formatInput={(d) => d3.format(".3f")(d)}
                                units="Mbps"
                                className="margin-b-15"
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapdRate"]: value,
                                  })
                                }
                                hideSpinner={true}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapdRate"]: DEFAULT_POLICY.shapdRate,
                                  });
                                }}
                              />
                              <CollapsablePanel title="Advanced Parameters*">
                                <NumberInputWithSpinner
                                  name="shapdBrate"
                                  label="Burst Rate"
                                  subLabel="(0=no burst)"
                                  min={0}
                                  max={100000}
                                  step={0.001}
                                  initialValue={shapdBrate}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBrate"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shapdBrateUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBrateUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBrate"]: DEFAULT_POLICY.shapdBrate,
                                      ["shapdBrateUnit"]:
                                        DEFAULT_POLICY.shapdBrateUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shapdBdur"
                                  label="Burst Duration"
                                  min={0}
                                  max={86400}
                                  initialValue={shapdBdur}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBdur"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units="seconds"
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBdur"]: DEFAULT_POLICY.shapdBdur,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shapdBthr"
                                  label="Burst Threshold"
                                  subLabel="(0=normal rate)"
                                  min={0}
                                  max={100000}
                                  initialValue={shapdBthr}
                                  step={0.001}
                                  formatInput={(d) => d3.format(".2f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBthr"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shapdBthrUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBthrUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBthr"]: DEFAULT_POLICY.shapdBthr,
                                      ["shapdBthrUnit"]:
                                        DEFAULT_POLICY.shapdBthrUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shapdBthrw"
                                  label="Burst Threshold Window"
                                  min={0}
                                  max={100000}
                                  initialValue={shapdBthrw}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBthrw"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units="seconds"
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdBthrw"]: DEFAULT_POLICY.shapdBthrw,
                                    });
                                  }}
                                />
                              </CollapsablePanel>
                            </div>
                          ) : null}
                        </div>
                        {/* <!-- Downlink per Flow --> */}
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Downlink shaping flow"
                              >
                                Flow number limit*:{" "}
                                <span className="font-bold">
                                  {flowLimitEnabled ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={flowLimitEnabled}
                              onChange={({ target }) => {
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["flowLimitEnabled"]: target.checked,
                                });
                              }}
                            />
                          </div>
                          {flowLimitEnabled ? (
                            <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shapdRateFlow"
                                label="Flow Limit"
                                min={0.0}
                                max={100000}
                                initialValue={shapdRateFlow}
                                formatInput={(d) => d3.format(".3f")(d)}
                                units="Flows"
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapdRateFlow"]: value,
                                  })
                                }
                                className="margin-b-15"
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapdRateFlow"]:
                                      DEFAULT_POLICY.shapdRateFlow,
                                  });
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                      </div>
                      {/* <!-- ************ Column B ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        {/* <!-- Uplink shaping per Subscriber: --> */}
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Uplink shaping"
                              >
                                Maximum subscriber uplink speed:{" "}
                                <span id="statusUplink" className="font-bold">
                                  {rateSubsUpEnabled ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={rateSubsUpEnabled}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["rateSubsUpEnabled"]: target.checked,
                                })
                              }
                            />
                          </div>
                          {rateSubsUpEnabled ? (
                            <div
                              className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0"
                              id="poliFlowShapdShow"
                            >
                              <NumberInputWithSpinner
                                name="shapuRate"
                                label="Rate"
                                min={0.0}
                                max={100000}
                                initialValue={shapuRate}
                                step={0.001}
                                formatInput={(d) => d3.format(".3f")(d)}
                                units="Mbps"
                                className="margin-b-15"
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapuRate"]: value,
                                  })
                                }
                                hideSpinner={true}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapuRate"]: DEFAULT_POLICY.shapuRate,
                                  });
                                }}
                              />
                              <CollapsablePanel title="Advanced Parameters*">
                                <NumberInputWithSpinner
                                  name="shapuBrate"
                                  label="Burst Rate"
                                  subLabel="(0=no burst)"
                                  min={0}
                                  max={100000}
                                  step={0.001}
                                  initialValue={shapuBrate}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBrate"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shapuBrateUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBrateUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBrate"]: DEFAULT_POLICY.shapuBrate,
                                      ["shapuBrateUnit"]:
                                        DEFAULT_POLICY.shapuBrateUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shapuBdur"
                                  label="Burst Duration"
                                  min={0}
                                  max={86400}
                                  initialValue={shapuBdur}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBdur"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units="seconds"
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBdur"]: DEFAULT_POLICY.shapuBdur,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shapuBthr"
                                  label="Burst Threshold"
                                  subLabel="(0=normal rate)"
                                  min={0}
                                  max={100000}
                                  initialValue={shapuBthr}
                                  step={0.001}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBthr"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shapuBthrUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBthrUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBthr"]: DEFAULT_POLICY.shapuBthr,
                                      ["shapuBthrUnit"]:
                                        DEFAULT_POLICY.shapuBthrUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shapuBthrw"
                                  label="Burst Threshold Window"
                                  min={0}
                                  max={100000}
                                  initialValue={shapuBthrw}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBthrw"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units="seconds"
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuBthrw"]: DEFAULT_POLICY.shapuBthrw,
                                    });
                                  }}
                                />
                              </CollapsablePanel>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Wrapper>
                )}
              </div>
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-10 padding-0">
                <small>
                  * The Block and ACM settings, the Flow Number Limit, and the
                  advanced rate parameters (burst, etc.) will apply to
                  subscribers with this policy assigned, but not to
                  subscriber-groups that have this policy assigned. Only the
                  rate limits will apply to subscriber groups.
                </small>
              </div>
              <ChangesWrapper>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 ">
                  {changes ? (
                    <PendingChanges>
                      <Circle />
                      <b>Pending Changes</b>
                    </PendingChanges>
                  ) : null}
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 align-right padding-r-0">
                  <div className="align-right margin-b-0">
                    <button
                      type="button"
                      className="btn btn-link waves-effect hidden-to-operators"
                      onClick={handleApply}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="btn btn-link waves-effect"
                      onClick={() => {
                        if (
                          location.pathname === "/viewStatusSubscriberGroups"
                        ) {
                          const subGroupsParams = { ...queryParams };
                          globalNavigate(
                            "viewStatusSubscriberGroups",
                            Object.keys(subGroupsParams).length > 0
                              ? subGroupsParams
                              : undefined,
                          );
                        } else if (returnView) {
                          const {
                            policy: _p,
                            returnView: _rv,
                            ...restParams
                          } = params;
                          globalNavigate(
                            returnView,
                            Object.keys(restParams).length > 0
                              ? restParams
                              : undefined,
                          );
                        } else {
                          globalNavigateBack();
                        }
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </ChangesWrapper>
            </div>
          </div>
        </div>
      </div>
      {showBlockModal ? (
        <BlockModal
          onClose={() => setShowBlockModal(false)}
          onApply={() =>
            setEditedPolicy({
              ...editedPolicy,
              ["bckEnabled"]: !editedPolicy.bckEnabled,
            })
          }
        />
      ) : null}
    </Wrapper>
  );
};

export default PolicyRateEdit;
